import type {
    BirdingHabitatMatchExplanation,
    BirdingHabitatRuleMatch,
    HabitatClassificationContext,
    HabitatClassificationRule,
    HabitatClassificationRuleKind
} from "./types";
import {normalizeNumber, normalizeSearchText} from "./helpers";

const DERIVED_SUBTYPE_RULE_IDS = new Set([
    'cloud-forest',
    'desert-riparian-corridor',
    'pinyon-juniper-woodland',
    'oak-savanna',
    'fynbos',
    'chaparral',
    'matorral',
    'maquis',
    'kwongan',
    'sagebrush-steppe',
    'boreal-muskeg',
    'everglades-marsh',
    'sonoran-desert-wash',
    'arctic-coastal-plain',
    'paramo',
    'puna'
]);

const DERIVED_DISPLAY_RULE_IDS = new Set([
    'lakeshore-woodland',
    'riverine-woodland',
    'subalpine-conifer-forest',
    'montane-forest',
    'montane-grassland',
    'managed-parkland',
    'rural-settlement',
    'urban-green-space',
    'agricultural-edge'
]);

const getRuleKind = (id: string): HabitatClassificationRuleKind => {
    if (DERIVED_SUBTYPE_RULE_IDS.has(id)) {
        return 'derived-subtype';
    }

    if (DERIVED_DISPLAY_RULE_IDS.has(id)) {
        return 'derived-display';
    }

    return 'canonical';
};

const getSearchText = (context: HabitatClassificationContext) => [
    context.primaryHabitat,
    context.dominantHabitatGroup,
    context.dominantWorldcoverLabel,
    context.waterContext,
    context.terrainSummary,
    context.elevationBand,
    ...context.secondaryHabitats,
    ...context.habitatMixLabels
]
    .map(value => normalizeSearchText(value))
    .filter(Boolean)
    .join(' | ');

const getRegionalSearchText = (context: HabitatClassificationContext) => [
    context.biomeName,
    context.ecoregionName,
    context.realm,
    ...context.coordinateTerms,
    ...context.biomeTerms,
    ...context.ecoregionTerms
]
    .map(value => normalizeSearchText(value))
    .filter(Boolean)
    .join(' | ');

const getBiogeographicSearchText = (context: HabitatClassificationContext) => [
    context.biomeName,
    context.ecoregionName,
    context.realm,
    ...context.biomeTerms,
    ...context.ecoregionTerms
]
    .map(value => normalizeSearchText(value))
    .filter(Boolean)
    .join(' | ');

const getRegionalValues = (context: HabitatClassificationContext) => [
    context.biomeName,
    context.ecoregionName,
    context.realm,
    ...context.coordinateTerms,
    ...context.biomeTerms,
    ...context.ecoregionTerms
]
    .map(value => normalizeSearchText(value))
    .filter(Boolean);

const getBiogeographicValues = (context: HabitatClassificationContext) => [
    context.biomeName,
    context.ecoregionName,
    context.realm,
    ...context.biomeTerms,
    ...context.ecoregionTerms
]
    .map(value => normalizeSearchText(value))
    .filter(Boolean);

const hasAny = (text: string, terms: string[]) => terms.some(term => text.includes(normalizeSearchText(term)));
const hasLocalAny = (context: HabitatClassificationContext, terms: string[]) => hasAny(getSearchText(context), terms);
const hasRegionalAny = (context: HabitatClassificationContext, terms: string[]) => hasAny(getRegionalSearchText(context), terms);
const hasBiogeographicAny = (context: HabitatClassificationContext, terms: string[]) => hasAny(getBiogeographicSearchText(context), terms);
const hasRegionalValue = (context: HabitatClassificationContext, terms: string[]) => {
    const normalizedTerms = terms.map(term => normalizeSearchText(term));
    return getRegionalValues(context).some(value => normalizedTerms.includes(value));
};
const hasBiogeographicValue = (context: HabitatClassificationContext, terms: string[]) => {
    const normalizedTerms = terms.map(term => normalizeSearchText(term));
    return getBiogeographicValues(context).some(value => normalizedTerms.includes(value));
};
const getStructuralLocalText = (context: HabitatClassificationContext) => [
    context.primaryHabitat,
    context.dominantHabitatGroup,
    context.dominantWorldcoverLabel
]
    .map(value => normalizeSearchText(value))
    .filter(Boolean)
    .join(' | ');
const hasStructuralLocalAny = (context: HabitatClassificationContext, terms: string[]) => hasAny(getStructuralLocalText(context), terms);
const hasStrongForestCover = (context: HabitatClassificationContext) => hasStructuralLocalAny(context, ['forest', 'woodland', 'tree', 'broadleaf', 'evergreen', 'conifer', 'canopy']);
const hasOpenGrassland = (context: HabitatClassificationContext) => hasLocalAny(context, ['grassland', 'savanna', 'open woodland', 'wooded grassland', 'prairie', 'meadow', 'steppe']);
const hasStrongOpenGrassland = (context: HabitatClassificationContext) => hasStructuralLocalAny(context, ['grassland', 'savanna', 'open woodland', 'wooded grassland', 'prairie', 'meadow', 'steppe', 'open country']);
const hasStrongShrubCover = (context: HabitatClassificationContext) => hasStructuralLocalAny(context, ['shrub', 'shrubland', 'scrub', 'chaparral', 'maquis', 'matorral', 'heath', 'brush', 'thicket', 'thornscrub']);
const hasWetlandCover = (context: HabitatClassificationContext) => hasLocalAny(context, ['wetland', 'marsh', 'swamp', 'bog', 'fen', 'mire', 'peat', 'pool', 'slough']);
const hasWaterCover = (context: HabitatClassificationContext) => hasLocalAny(context, ['open water', 'permanent water', 'water body', 'lake', 'river', 'stream', 'creek', 'pond', 'channel', 'water']);
const hasDesertCover = (context: HabitatClassificationContext) => hasLocalAny(context, ['desert', 'dune', 'sand', 'xeric', 'arid', 'bare ground', 'bare', 'basin']);
const hasExplicitDesertAriditySignal = (context: HabitatClassificationContext) =>
    hasLocalAny(context, ['desert', 'arid', 'xeric', 'dune', 'sand', 'sandy', 'salt flat', 'playa', 'badlands', 'cold desert', 'temperate desert', 'rain shadow', 'rain-shadow'])
    || hasRegionalAny(context, ['great basin desert', 'patagonian desert', 'gobi desert', 'temperate desert', 'cold desert', 'xeric shrubland', 'deserts and xeric', 'deserts & xeric', 'rain shadow', 'rain-shadow']);
const hasRockyCover = (context: HabitatClassificationContext) => hasLocalAny(context, ['rock', 'rocky', 'scree', 'talus', 'cliff', 'bare rock', 'boulder', 'canyon', 'stone']);
const hasHotspotNameAny = (context: HabitatClassificationContext, terms: string[]) => {
    const hotspotName = normalizeSearchText(context.hotspotName);
    if (terms.some(term => hotspotName.includes(normalizeSearchText(term)))) {
        return true;
    }

    return context.hotspotNameTerms.some(term => terms.includes(term));
};
const getNumericEcoDataField = (context: HabitatClassificationContext, field: string) => {
    return normalizeNumber((context.ecoData as Record<string, unknown>)[field]);
};
const getWaterFraction = (context: HabitatClassificationContext) => getNumericEcoDataField(context, 'habitat_water_fraction');
const getForestFraction = (context: HabitatClassificationContext) => getNumericEcoDataField(context, 'habitat_forest_fraction');
const getWetlandFraction = (context: HabitatClassificationContext) => getNumericEcoDataField(context, 'habitat_wetland_fraction');
const hasMeaningfulForestMatrix = (context: HabitatClassificationContext) => {
    const forestFraction = getForestFraction(context);
    return (forestFraction !== undefined && forestFraction >= 0.15) || hasLocalAny(context, ['forest', 'woodland', 'tree']);
};
const hasDominantFreshHerbaceousWetland = (context: HabitatClassificationContext) => {
    const wetlandFraction = getWetlandFraction(context);
    return (wetlandFraction !== undefined && wetlandFraction >= 0.5)
        && hasLocalAny(context, ['wetland', 'marsh', 'herbaceous wetland', 'emergent']);
};
const hasGreatLakesRegionalContext = (context: HabitatClassificationContext) => hasRegionalAny(context, ['great lakes']);
const hasDirectWaterEdgeContext = (context: HabitatClassificationContext) =>
    context.waterContext === 'At water / water edge'
    || context.waterContext === 'On the shoreline'
    || context.waterContext === 'Near water'
    || hasLocalAny(context, ['water edge', 'water-edge', 'lake edge', 'lakeshore', 'shoreline']);
const hasLakeshoreEdgeContext = (context: HabitatClassificationContext) =>
    context.waterContext === 'On the shoreline'
    || hasLocalAny(context, ['lake edge', 'lakeshore', 'shoreline'])
    || hasHotspotNameAny(context, ['lake', 'lakeshore', 'shore'])
    || ((context.waterContext === 'At water / water edge' || context.waterContext === 'Near water') && hasGreatLakesRegionalContext(context));
const hasStrongCoastalContext = (context: HabitatClassificationContext) =>
    context.coastalContext === 'coastal'
    || (context.distanceToCoastM !== undefined && context.distanceToCoastM !== null && context.distanceToCoastM <= 1000);
const hasNearCoastalContext = (context: HabitatClassificationContext) =>
    hasStrongCoastalContext(context)
    || context.coastalContext === 'near_coastal'
    || (context.distanceToCoastM !== undefined && context.distanceToCoastM !== null && context.distanceToCoastM <= 10000);
const hasExplicitLakeEvidence = (context: HabitatClassificationContext) =>
    hasHotspotNameAny(context, ['lake', 'loch', 'mere', 'reservoir', 'pond'])
    || hasLocalAny(context, ['freshwater lake', 'oxbow lake', 'large pond', 'lake', 'loch', 'mere'])
    || hasRegionalAny(context, ['freshwater lake', 'oxbow lake']);
const hasMarineWaterContext = (context: HabitatClassificationContext) => hasLocalAny(context, ['ocean', 'sea', 'marine', 'coastal water', 'bay', 'gulf', 'pelagic', 'nearshore', 'offshore']) || hasRegionalAny(context, ['marine', 'coastal', 'ocean', 'sea']);
const hasCoastalContext = (context: HabitatClassificationContext) => hasNearCoastalContext(context) || hasLocalAny(context, ['coastal', 'shore', 'shoreline', 'beach', 'bay', 'gulf', 'estuary', 'tidal', 'intertidal', 'nearshore']) || hasRegionalAny(context, ['coastal', 'shore', 'estuary', 'tidal', 'marine']);
const hasShorelineSubstrate = (context: HabitatClassificationContext) => hasLocalAny(context, ['rock', 'rocky', 'reef', 'bedrock', 'sand', 'sandy', 'beach', 'strand', 'mud', 'mudflat', 'flat', 'intertidal', 'pool', 'tidepool']);
const hasAgriculturalContext = (context: HabitatClassificationContext) => hasLocalAny(context, ['cropland', 'agricultural', 'farm', 'field', 'row crop', 'cultivated', 'orchard', 'pasture', 'hayfield', 'rangeland', 'paddock', 'plantation', 'garden']);
const hasUrbanContext = (context: HabitatClassificationContext) => hasLocalAny(context, ['urban', 'built-up', 'built up', 'developed', 'settlement', 'residential', 'industrial', 'city', 'road-dense', 'quarry', 'pit', 'mine', 'landfill']);
const hasExplicitAgriculturalUse = (context: HabitatClassificationContext) => hasLocalAny(context, ['cropland', 'farm', 'field', 'row crop', 'cultivated', 'orchard', 'pasture', 'hayfield', 'rangeland', 'paddock', 'plantation', 'garden', 'grazed grassland', 'livestock']);
const hasArtificialWaterContext = (context: HabitatClassificationContext) => hasLocalAny(context, ['reservoir', 'impoundment', 'dam', 'retention basin', 'artificial lake', 'retention pond', 'farm pond', 'ornamental pond', 'canal', 'ditch', 'drainage channel', 'irrigation canal', 'aqueduct']);
const hasUrbanGreenSpaceSignal = (context: HabitatClassificationContext) =>
    hasLocalAny(context, ['park', 'green space', 'greenspace', 'urban park', 'city park', 'park woodland', 'parkland', 'garden', 'arboretum', 'cemetery', 'grassland', 'lawn', 'tree', 'woodland'])
    || hasHotspotNameAny(context, ['park', 'green space', 'greenspace', 'garden', 'arboretum', 'cemetery', 'woodland', 'woods']);
const hasConfirmedOceanContext = (context: HabitatClassificationContext) =>
    context.marineContextIsOcean === true
    && context.marineContext !== 'unknown';
const hasNearshoreMarineContext = (context: HabitatClassificationContext) =>
    hasConfirmedOceanContext(context)
    && context.marineContext === 'nearshore_marine';
const hasPelagicMarineContext = (context: HabitatClassificationContext) =>
    hasConfirmedOceanContext(context)
    && context.marineContext === 'pelagic_water';
const MANAGED_PARKLAND_TERMS = [
    'golf course',
    'golf club',
    'country club',
    'golf links',
    'links course',
    'cemetery',
    'memorial park',
    'campus',
    'university grounds',
    'college grounds',
    'arboretum',
    'recreation complex',
    'sports complex',
    'managed parkland',
    'landscaped park',
    'landscaped grounds',
    'institutional campus'
];
const MANAGED_PARKLAND_AGRICULTURAL_EXCLUSIONS = [
    'cropland',
    'farm',
    'field',
    'row crop',
    'cultivated',
    'arable',
    'pasture',
    'hayfield',
    'grazed grassland',
    'rangeland',
    'paddock',
    'livestock',
    'plantation',
    'orchard'
];
const getManagedParklandExplicitText = (context: HabitatClassificationContext) => [
    context.hotspotName,
    context.primaryHabitat,
    ...context.secondaryHabitats
]
    .map(value => normalizeSearchText(value))
    .filter(Boolean)
    .join(' | ');
const hasManagedParklandExplicitSignal = (context: HabitatClassificationContext) =>
    hasHotspotNameAny(context, MANAGED_PARKLAND_TERMS)
    || hasAny(getManagedParklandExplicitText(context), MANAGED_PARKLAND_TERMS);
const hasManagedParklandStructure = (context: HabitatClassificationContext) =>
    hasLocalAny(context, ['grassland', 'open country', 'managed grass', 'lawn', 'turf', 'meadow'])
    || hasLocalAny(context, ['urban', 'built-up', 'built up', 'developed'])
    || hasLocalAny(context, ['tree', 'woodland', 'forest', 'woodland edge', 'edge'])
    || hasLocalAny(context, ['pond', 'ornamental pond', 'retention pond', 'small water body'])
    || hasLocalAny(context, ['mosaic', 'grass/tree', 'grass tree']);
const hasDominantNaturalManagedParklandExclusion = (context: HabitatClassificationContext) =>
    hasStructuralLocalAny(context, ['desert', 'dune', 'sand', 'xeric', 'arid', 'bare ground', 'bare'])
    || hasStructuralLocalAny(context, ['coastal', 'shore', 'shoreline', 'beach', 'tidal', 'intertidal'])
    || hasLocalAny(context, MANAGED_PARKLAND_AGRICULTURAL_EXCLUSIONS);
const getManagedParklandReasons = (context: HabitatClassificationContext) => {
    const reasons: string[] = [];
    const explicitText = getManagedParklandExplicitText(context);
    const hasGolfOrParklandNameSignal = hasHotspotNameAny(context, [
        'golf course',
        'golf club',
        'country club',
        'golf links',
        'links course',
        'managed parkland',
        'landscaped park',
        'landscaped grounds',
        'memorial park'
    ]);
    const hasManagedLandscapeSignal = hasAny(explicitText, MANAGED_PARKLAND_TERMS);
    const hasGrassStructure = hasLocalAny(context, ['grassland', 'open country', 'managed grass', 'lawn', 'turf', 'meadow']);
    const hasTreeStructure = hasLocalAny(context, ['tree', 'woodland', 'forest', 'woodland edge', 'edge']);
    const hasDevelopedStructure = hasLocalAny(context, ['urban', 'built-up', 'built up', 'developed']);
    const hasArtificialWaterStructure = hasLocalAny(context, ['pond', 'ornamental pond', 'retention pond', 'small water body']);

    if (hasGolfOrParklandNameSignal) {
        reasons.push('Golf-course or parkland place-name signal');
    }

    if (hasManagedLandscapeSignal) {
        reasons.push('Managed recreational landscape');
    }

    if (hasGrassStructure && (hasTreeStructure || hasDevelopedStructure)) {
        reasons.push('Open turf with scattered trees or developed edges');
    }

    if (hasGrassStructure && hasTreeStructure && (hasDevelopedStructure || hasArtificialWaterStructure)) {
        reasons.push('Managed grass, tree, and developed habitat mosaic');
    }

    return reasons.length ? reasons : ['Managed recreational landscape'];
};
const RURAL_SETTLEMENT_TERMS = [
    'house',
    'home',
    'residence',
    'residential',
    'homestead',
    'farmhouse',
    'ranch headquarters',
    'lodge',
    'hotel',
    'inn',
    'ecolodge',
    'resort',
    'cabins',
    'campground',
    'camp',
    'village',
    'hamlet',
    'settlement',
    'school',
    'church',
    'visitor center',
    'research station',
    'field station',
    'hostel',
    'guesthouse',
    'estate',
    'property'
];
const RURAL_SETTLEMENT_STRONG_PLACE_TERMS = [
    'residence',
    'homestead',
    'farmhouse',
    'ranch headquarters',
    'lodge',
    'hotel',
    'inn',
    'ecolodge',
    'resort',
    'cabins',
    'campground',
    'village',
    'hamlet',
    'school',
    'church',
    'research station',
    'field station',
    'hostel',
    'guesthouse'
];
const RURAL_SETTLEMENT_NATURAL_FEATURE_TERMS = [
    'preserve',
    'reserve',
    'wildlife refuge',
    'nature preserve',
    'natural area',
    'prairie',
    'marsh',
    'wetland',
    'lake',
    'pond',
    'river',
    'shore',
    'beach',
    'forest trail',
    'trail'
];
const getDevelopedFraction = (context: HabitatClassificationContext) => getNumericEcoDataField(context, 'habitat_developed_fraction');
const getRuralSettlementExplicitText = (context: HabitatClassificationContext) => [
    context.hotspotName,
    context.primaryHabitat,
    ...context.secondaryHabitats
]
    .map(value => normalizeSearchText(value))
    .filter(Boolean)
    .join(' | ');
const hasRuralSettlementExplicitSignal = (context: HabitatClassificationContext) =>
    hasHotspotNameAny(context, RURAL_SETTLEMENT_TERMS)
    || hasAny(getRuralSettlementExplicitText(context), RURAL_SETTLEMENT_TERMS);
const hasStrongRuralSettlementPlaceSignal = (context: HabitatClassificationContext) =>
    hasHotspotNameAny(context, RURAL_SETTLEMENT_STRONG_PLACE_TERMS)
    || hasAny(getRuralSettlementExplicitText(context), RURAL_SETTLEMENT_STRONG_PLACE_TERMS);
const hasNamedLowDensitySettlementSignal = (context: HabitatClassificationContext) => {
    const developedFraction = getDevelopedFraction(context);
    const hotspotName = normalizeSearchText(context.hotspotName);

    return !!hotspotName
        && !hasAny(hotspotName, RURAL_SETTLEMENT_NATURAL_FEATURE_TERMS)
        && developedFraction !== undefined
        && developedFraction >= 0.05
        && developedFraction <= 0.45;
};
const hasRuralSettlementMatrix = (context: HabitatClassificationContext) => {
    const developedFraction = getDevelopedFraction(context);

    return hasLocalAny(context, ['rural', 'remote', 'countryside', 'agricultural', 'farmland', 'forest', 'woodland', 'grassland', 'pasture', 'shrubland', 'scrub', 'desert', 'wetland', 'riparian'])
        || hasRegionalAny(context, ['rural', 'remote', 'countryside', 'agricultural', 'farmland', 'forest', 'woodland', 'grassland', 'prairie', 'shrubland', 'scrub', 'desert', 'wetland', 'riparian'])
        || (developedFraction !== undefined && developedFraction >= 0.05 && developedFraction <= 0.45);
};
const hasDenseUrbanContext = (context: HabitatClassificationContext) => {
    const developedFraction = getDevelopedFraction(context);

    return (developedFraction !== undefined && developedFraction >= 0.65)
        || hasLocalAny(context, ['dense urban', 'continuous urban', 'suburban', 'metro', 'metropolitan', 'commercial district', 'residential district', 'industrial district'])
        || (
            hasStructuralLocalAny(context, ['urban', 'built-up', 'built up', 'developed'])
            && hasLocalAny(context, ['residential', 'industrial', 'commercial'])
            && !hasRegionalAny(context, ['rural', 'remote', 'countryside', 'agricultural', 'farmland'])
        );
};
const hasRuralSettlementAgriculturalExclusion = (context: HabitatClassificationContext) =>
    hasExplicitAgriculturalUse(context);
const hasRuralSettlementNaturalFeatureExclusion = (context: HabitatClassificationContext) =>
    !hasStrongRuralSettlementPlaceSignal(context)
    && hasHotspotNameAny(context, RURAL_SETTLEMENT_NATURAL_FEATURE_TERMS);
const hasDominantRuralSettlementNaturalExclusion = (context: HabitatClassificationContext) =>
    hasStructuralLocalAny(context, ['open water', 'permanent water', 'water body', 'lake', 'river', 'pond', 'wetland', 'marsh', 'swamp', 'coastal', 'shore', 'shoreline', 'beach', 'tidal', 'intertidal']);
const hasRuralSettlementSignal = (context: HabitatClassificationContext) =>
    (hasRuralSettlementExplicitSignal(context) || hasNamedLowDensitySettlementSignal(context))
    && hasRuralSettlementMatrix(context)
    && !hasDenseUrbanContext(context)
    && !hasRuralSettlementAgriculturalExclusion(context)
    && !hasRuralSettlementNaturalFeatureExclusion(context)
    && !hasDominantRuralSettlementNaturalExclusion(context)
    && !hasManagedParklandExplicitSignal(context);
const getRuralSettlementReasons = (context: HabitatClassificationContext) => {
    const reasons: string[] = [];
    const explicitText = getRuralSettlementExplicitText(context);
    const developedFraction = getDevelopedFraction(context);
    const hasLodgingSignal = hasAny(explicitText, ['lodge', 'hotel', 'inn', 'ecolodge', 'resort', 'hostel', 'guesthouse']);
    const hasResidenceSignal = hasAny(explicitText, ['house', 'home', 'residence', 'homestead', 'farmhouse']);
    const hasSettlementPlaceSignal = hasAny(explicitText, ['village', 'hamlet', 'settlement']);
    const hasForestMatrix = hasLocalAny(context, ['forest', 'woodland']) || hasRegionalAny(context, ['forest', 'woodland']);
    const hasOpenCountryMatrix = hasLocalAny(context, ['grassland', 'open country', 'prairie']) || hasRegionalAny(context, ['grassland', 'prairie']);
    const hasAgriculturalMatrix = hasLocalAny(context, ['agricultural', 'farmland']) || hasRegionalAny(context, ['agricultural', 'farmland']);
    const hasLowDensityDevelopment = developedFraction !== undefined && developedFraction >= 0.05 && developedFraction <= 0.45;

    if (hasLodgingSignal) {
        reasons.push('Lodge or hotel in a rural setting');
        reasons.push('Place-name evidence for a rural residence or lodging property');
    } else if (hasResidenceSignal) {
        reasons.push('Rural human settlement');
        reasons.push('Place-name evidence for a rural residence or lodging property');
    } else if (hasSettlementPlaceSignal || hasNamedLowDensitySettlementSignal(context)) {
        reasons.push('Small settlement within open country');
    }

    if (hasForestMatrix) {
        reasons.push('Low-density development surrounded by forest');
    } else if (hasAgriculturalMatrix) {
        reasons.push('Developed site in an agricultural matrix');
    } else if (hasOpenCountryMatrix) {
        reasons.push('Built property within a natural landscape');
    } else if (hasLowDensityDevelopment) {
        reasons.push('Rural human settlement');
    }

    return Array.from(new Set(reasons.length ? reasons : ['Rural human settlement']));
};
const getGrasslandFraction = (context: HabitatClassificationContext) => getNumericEcoDataField(context, 'habitat_grassland_fraction');
const MONTANE_GRASSLAND_STRUCTURE_TERMS = [
    'grassland',
    'mountain meadow',
    'montane meadow',
    'upland meadow',
    'high pasture',
    'mountain pasture',
    'upland pasture',
    'grassy slope',
    'upland grassland',
    'high-elevation grassland',
    'open mountain',
    'open high country'
];
const MONTANE_GRASSLAND_ELEVATION_TERMS = [
    'montane terrain',
    'subalpine terrain',
    'alpine terrain',
    'montane',
    'mountain meadow',
    'alpine meadow',
    'subalpine meadow',
    'high mountain',
    'high elevation',
    'above treeline',
    'upland'
];
const hasMeaningfulGrasslandMix = (context: HabitatClassificationContext) => {
    const grasslandFraction = getGrasslandFraction(context);
    return grasslandFraction !== undefined && grasslandFraction >= 0.25;
};
const hasMontaneGrasslandStructure = (context: HabitatClassificationContext) =>
    hasStrongOpenGrassland(context)
    || hasMeaningfulGrasslandMix(context)
    || hasLocalAny(context, MONTANE_GRASSLAND_STRUCTURE_TERMS)
    || hasHotspotNameAny(context, MONTANE_GRASSLAND_STRUCTURE_TERMS);
const hasMontaneGrasslandElevation = (context: HabitatClassificationContext) =>
    context.elevationBand === 'montane'
    || context.elevationBand === 'subalpine'
    || context.elevationBand === 'alpine'
    || hasLocalAny(context, MONTANE_GRASSLAND_ELEVATION_TERMS)
    || hasHotspotNameAny(context, MONTANE_GRASSLAND_ELEVATION_TERMS);
const hasMontaneGrasslandExclusion = (context: HabitatClassificationContext) =>
    hasWetlandCover(context)
    || hasWaterCover(context)
    || hasExplicitAgriculturalUse(context)
    || hasLocalAny(context, ['tundra', 'permafrost', 'moss', 'lichen', 'bog', 'fen', 'marsh', 'wetland'])
    || (
        hasRockyCover(context)
        && !hasStrongOpenGrassland(context)
        && !hasMeaningfulGrasslandMix(context)
    )
    || (
        hasDesertCover(context)
        && hasExplicitDesertAriditySignal(context)
        && !hasStrongOpenGrassland(context)
    );
const hasMontaneGrasslandSignal = (context: HabitatClassificationContext) =>
    hasMontaneGrasslandStructure(context)
    && hasMontaneGrasslandElevation(context)
    && !hasRegionalValue(context, ['tropical', 'subtropical'])
    && !hasRegionalAny(context, ['tropical and subtropical', 'tropical & subtropical', 'paramo', 'páramo', 'puna'])
    && !hasMontaneGrasslandExclusion(context);
const getMontaneGrasslandReasons = (context: HabitatClassificationContext) => {
    const reasons: string[] = [];

    if (hasStrongOpenGrassland(context) || hasMeaningfulGrasslandMix(context)) {
        reasons.push('Grassland or open-country structure');
    }
    if (context.elevationBand === 'montane' || hasLocalAny(context, ['montane terrain', 'montane'])) {
        reasons.push('Montane terrain');
    } else if (context.elevationBand === 'subalpine' || context.elevationBand === 'alpine' || hasLocalAny(context, ['subalpine terrain', 'alpine terrain', 'alpine meadow', 'subalpine meadow', 'above treeline'])) {
        reasons.push('Subalpine or alpine terrain');
    }
    if (hasLocalAny(context, ['mountain meadow', 'montane meadow', 'upland meadow', 'high pasture', 'mountain pasture', 'upland pasture']) || hasHotspotNameAny(context, ['mountain meadow', 'montane meadow', 'upland meadow', 'high pasture', 'mountain pasture', 'upland pasture'])) {
        reasons.push('Mountain meadow or upland pasture context');
    }
    if (hasLocalAny(context, ['high-elevation grassland', 'high mountain', 'high elevation']) || hasHotspotNameAny(context, ['high-elevation grassland', 'high mountain', 'high elevation'])) {
        reasons.push('High-elevation grassland');
    }
    if (hasLocalAny(context, ['open mountain', 'open high country', 'grassy slope']) || hasHotspotNameAny(context, ['open mountain', 'open high country', 'grassy slope'])) {
        reasons.push('Open mountain habitat');
    }
    if (hasLocalAny(context, ['bare', 'bare / sparse', 'scree', 'talus', 'rock', 'rocky'])) {
        reasons.push('Grassland and exposed-rock mosaic');
    }

    return Array.from(new Set(reasons.length ? reasons : ['Grassland or open-country structure', 'Montane terrain']));
};

export const BIRDING_HABITAT_RULES: HabitatClassificationRule[] = [
    {
        id: 'iucn-intertidal-mangrove-roots',
        label: 'Intertidal Mangrove Roots',
        family: 'Wetland',
        kind: getRuleKind('iucn-intertidal-mangrove-roots'),
        priority: 315,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['mangrove']) &&
            hasLocalAny(context, ['water', 'shoreline', 'tidal', 'estuary', 'intertidal', 'shore']),
        reasons: () => ['Mangrove signal', 'Intertidal, estuarine, or shoreline water context']
    },
    {
        id: 'iucn-salt-marsh',
        label: 'Salt Marsh',
        family: 'Wetland',
        kind: getRuleKind('iucn-salt-marsh'),
        priority: 310,
        confidence: 'medium',
        matches: (context) =>
            hasLocalAny(context, ['marsh', 'salt marsh', 'tidal marsh']) &&
            (hasLocalAny(context, ['coastal', 'tidal', 'saline', 'salt', 'estuary', 'shoreline']) || hasRegionalAny(context, ['coastal', 'estuary', 'tidal'])),
        reasons: () => ['Marsh or wetland cover', 'Coastal, tidal, saline, or estuarine context']
    },
    {
        id: 'iucn-estuary',
        label: 'Estuary',
        family: 'Water',
        kind: getRuleKind('iucn-estuary'),
        priority: 305,
        confidence: 'low',
        matches: (context) =>
            (hasWaterCover(context) || hasWetlandCover(context)) &&
            (hasLocalAny(context, ['estuary', 'tidal river', 'bay mouth', 'delta mouth', 'coastal river']) || hasRegionalAny(context, ['estuary'])) &&
            !hasLocalAny(context, ['mangrove', 'salt marsh', 'tidal marsh']),
        reasons: () => ['Water or wetland context', 'Estuary, tidal-river, or brackish-mouth signals']
    },
    {
        id: 'iucn-coastal-brackish-lagoon',
        label: 'Coastal Brackish Lagoon',
        family: 'Water',
        kind: getRuleKind('iucn-coastal-brackish-lagoon'),
        priority: 300,
        confidence: 'low',
        matches: (context) =>
            hasWaterCover(context) &&
            hasLocalAny(context, ['lagoon', 'brackish', 'saline', 'barrier', 'marine lake']) &&
            hasCoastalContext(context) &&
            !hasLocalAny(context, ['reef', 'coral', 'tidepool', 'tidal pool', 'aquaculture', 'shrimp pond', 'prawn pond', 'culture pond']),
        reasons: () => ['Water context', 'Coastal lagoon, brackish, saline, barrier, or marine-lake signals']
    },
    {
        id: 'iucn-coastal-freshwater-lake',
        label: 'Coastal Freshwater Lake',
        family: 'Water',
        kind: getRuleKind('iucn-coastal-freshwater-lake'),
        priority: 295,
        confidence: 'low',
        matches: (context) =>
            hasWaterCover(context) &&
            hasLocalAny(context, ['freshwater lake', 'freshwater pond', 'pond', 'lake']) &&
            hasLocalAny(context, ['coastal', 'shore', 'dune', 'barrier', 'island']) &&
            !hasLocalAny(context, ['brackish', 'saline', 'estuary', 'lagoon']),
        reasons: () => ['Freshwater lake or pond context', 'Coastal, barrier, dune, shore, or island setting']
    },
    {
        id: 'iucn-rocky-shoreline',
        label: 'Rocky Shoreline',
        family: 'Water',
        kind: getRuleKind('iucn-rocky-shoreline'),
        priority: 290,
        confidence: 'low',
        matches: (context) =>
            hasCoastalContext(context) &&
            hasLocalAny(context, ['rock', 'rocky', 'cliff', 'reef', 'bedrock']) &&
            !hasLocalAny(context, ['coral', 'barrier reef', 'fringing reef', 'lagoon reef', 'tidepool', 'tidal pool', 'offshore island', 'island']),
        reasons: () => ['Coastal or shoreline context', 'Rocky, cliff, reef, or bedrock shoreline signals']
    },
    {
        id: 'iucn-sandy-shoreline',
        label: 'Sandy Shoreline',
        family: 'Water',
        kind: getRuleKind('iucn-sandy-shoreline'),
        priority: 285,
        confidence: 'low',
        matches: (context) =>
            hasCoastalContext(context) &&
            hasLocalAny(context, ['sand', 'sandy beach', 'beach', 'strand']),
        reasons: () => ['Coastal or shoreline context', 'Sand, beach, or strand signals']
    },
    {
        id: 'iucn-sea-cliff-rocky-island',
        label: 'Sea Cliff and Rocky Island',
        family: 'Mountain',
        kind: getRuleKind('iucn-sea-cliff-rocky-island'),
        priority: 270,
        confidence: 'low',
        matches: (context) =>
            (hasCoastalContext(context) || hasLocalAny(context, ['island', 'offshore island'])) &&
            hasLocalAny(context, ['cliff', 'rocky', 'rock', 'steep terrain', 'offshore island']) &&
            !hasLocalAny(context, ['pool', 'tidepool', 'tidal pool', 'intertidal']),
        reasons: () => ['Coastal or island context', 'Cliff, rock, rocky-island, or steep-terrain signals']
    },
    {
        id: 'iucn-mud-flat',
        label: 'Mud Flat',
        family: 'Wetland',
        kind: getRuleKind('iucn-mud-flat'),
        priority: 280,
        confidence: 'low',
        matches: (context) =>
            hasCoastalContext(context) &&
            hasLocalAny(context, ['mud', 'mudflat', 'flat', 'intertidal', 'estuary', 'exposed sediment']) &&
            !hasLocalAny(context, ['marsh', 'mangrove', 'tidepool', 'tidal pool', 'rocky']),
        reasons: () => ['Coastal or tidal context', 'Mudflat, intertidal flat, or exposed-sediment signals']
    },
    {
        id: 'iucn-tidepool',
        label: 'Tidepool',
        family: 'Wetland',
        kind: getRuleKind('iucn-tidepool'),
        priority: 265,
        confidence: 'low',
        matches: (context) =>
            hasCoastalContext(context) &&
            hasLocalAny(context, ['pool', 'tidepool', 'tidal pool', 'intertidal', 'reef-flat']) &&
            hasLocalAny(context, ['rock', 'rocky', 'reef', 'shoreline']),
        reasons: () => ['Coastal rocky shoreline context', 'Pool, tidepool, or intertidal pool signals']
    },
    {
        id: 'iucn-coral-reef',
        label: 'Coral Reef',
        family: 'Water',
        kind: getRuleKind('iucn-coral-reef'),
        priority: 263,
        confidence: 'low',
        matches: (context) =>
            hasMarineWaterContext(context) &&
            hasLocalAny(context, ['reef', 'coral', 'lagoon reef', 'barrier reef', 'fringing reef', 'reef-flat']),
        reasons: () => ['Marine or coastal water context', 'Coral or reef signals']
    },
    {
        id: 'iucn-seagrass-bed',
        label: 'Seagrass Bed',
        family: 'Water',
        priority: 262,
        confidence: 'low',
        matches: (context) =>
            hasMarineWaterContext(context) &&
            hasLocalAny(context, ['seagrass', 'eelgrass', 'submerged aquatic vegetation', 'shallow vegetated bay']),
        reasons: () => ['Marine or coastal water context', 'Seagrass, eelgrass, or submerged-vegetation signals']
    },
    {
        id: 'iucn-kelp-bed',
        label: 'Kelp Bed',
        family: 'Water',
        priority: 261,
        confidence: 'low',
        matches: (context) =>
            hasMarineWaterContext(context) &&
            hasLocalAny(context, ['kelp', 'macroalgae', 'seaweed bed', 'rocky subtidal', 'cold nearshore reef']),
        reasons: () => ['Marine or coastal water context', 'Kelp, macroalgae, or rocky-subtidal signals']
    },
    {
        id: 'iucn-coastal-sand-dune',
        label: 'Coastal Sand Dune',
        family: 'Desert',
        priority: 275,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['sand', 'dune', 'bare', 'sparsely vegetated']) &&
            hasLocalAny(context, ['coastal', 'shore', 'beach', 'barrier', 'island']) &&
            !hasLocalAny(context, ['rock', 'rocky', 'cliff', 'bedrock']),
        reasons: () => ['Sand, dune, bare, or sparse cover', 'Coastal, barrier, beach, shore, or island context']
    },
    {
        id: 'iucn-mangrove-forest',
        label: 'Mangrove Forest',
        family: 'Forest',
        priority: 320,
        confidence: 'high',
        matches: (context) =>
            hasStrongForestCover(context) &&
            (hasLocalAny(context, ['mangrove']) || hasRegionalAny(context, ['mangrove'])),
        reasons: () => ['Mangrove signal', 'Forest or tree cover']
    },
    {
        id: 'iucn-swamp-forest',
        label: 'Swamp Forest',
        family: 'Forest',
        priority: 260,
        confidence: 'high',
        matches: (context) =>
            hasStrongForestCover(context) &&
            hasLocalAny(context, ['swamp', 'wetland', 'marsh', 'flooded forest', 'flooded']),
        reasons: () => ['Forest or tree cover', 'Swamp or flooded wetland context']
    },
    {
        id: 'iucn-moist-montane-forest',
        label: 'Moist Montane Forest',
        family: 'Forest',
        priority: 255,
        confidence: 'high',
        matches: (context) =>
            hasStrongForestCover(context) &&
            (
                hasLocalAny(context, ['montane', 'subalpine', 'alpine', 'mountain', 'highland', 'cloud', 'humid', 'moist'])
                || hasRegionalAny(context, ['montane', 'andean', 'cloud forest'])
            ) &&
            (hasBiogeographicAny(context, ['tropical', 'subtropical', 'humid', 'moist']) || hasBiogeographicAny(context, ['neotropical', 'afrotropical', 'indo-malayan', 'australasian'])),
        reasons: () => ['Forest cover', 'Moist montane or cloud-forest context']
    },
    {
        id: 'iucn-moist-lowland-tropical-forest',
        label: 'Moist Lowland Tropical Forest',
        family: 'Forest',
        priority: 250,
        confidence: 'high',
        matches: (context) =>
            hasStrongForestCover(context) &&
            hasBiogeographicAny(context, ['tropical', 'subtropical', 'rainforest', 'rain forest', 'humid forest', 'moist broadleaf', 'neotropical', 'afrotropical', 'indo-malayan', 'australasian']) &&
            !hasLocalAny(context, ['montane', 'subalpine', 'alpine', 'mountain', 'highland', 'cloud']) &&
            !hasLocalAny(context, ['degraded', 'disturbed', 'secondary', 'cleared', 'fragmented', 'regrowth', 'heavily altered']) &&
            !hasLocalAny(context, ['dry', 'xeric', 'thorn']) &&
            !hasBiogeographicAny(context, ['montane', 'andean', 'cloud forest']) &&
            !hasBiogeographicAny(context, ['deserts & xeric shrublands', 'xeric shrublands', 'mezquital']),
        reasons: () => ['Forest cover', 'Lowland tropical or subtropical moist context']
    },
    {
        id: 'iucn-dry-tropical-forest',
        label: 'Dry Tropical Forest',
        family: 'Forest',
        priority: 245,
        confidence: 'high',
        matches: (context) =>
            hasStrongForestCover(context) &&
            hasBiogeographicAny(context, ['tropical', 'subtropical', 'neotropical', 'afrotropical', 'indo-malayan', 'australasian']) &&
            (hasLocalAny(context, ['dry', 'deciduous tropical', 'thorn forest', 'thorn']) || hasBiogeographicAny(context, ['dry forest', 'seasonally dry', 'dry broadleaf', 'xeric'])) &&
            !hasLocalAny(context, ['degraded', 'disturbed', 'secondary', 'cleared', 'fragmented', 'regrowth', 'heavily altered']),
        reasons: () => ['Forest cover', 'Tropical or subtropical dry-forest context']
    },
    {
        id: 'iucn-temperate-forest',
        label: 'Temperate Forest',
        family: 'Forest',
        priority: 240,
        confidence: 'high',
        matches: (context) =>
            hasStrongForestCover(context) &&
            hasBiogeographicAny(context, ['temperate', 'temperate forest', 'temperate broadleaf', 'mixed forest']) &&
            !hasBiogeographicValue(context, ['tropical', 'subtropical', 'boreal', 'subarctic', 'subantarctic']),
        reasons: () => ['Forest cover', 'Temperate forest biome or ecoregion context']
    },
    {
        id: 'iucn-boreal-forest',
        label: 'Boreal Forest',
        family: 'Forest',
        priority: 235,
        confidence: 'high',
        matches: (context) =>
            hasStrongForestCover(context) &&
            (hasLocalAny(context, ['boreal', 'taiga', 'spruce', 'fir']) || hasRegionalAny(context, ['boreal', 'taiga'])),
        reasons: () => ['Forest cover', 'Boreal or taiga context']
    },
    {
        id: 'iucn-subarctic-forest',
        label: 'Subarctic Forest',
        family: 'Forest',
        priority: 230,
        confidence: 'medium',
        matches: (context) =>
            hasStrongForestCover(context) &&
            (
                hasRegionalAny(context, ['subarctic'])
                || hasLocalAny(context, ['treeline', 'tundra-edge'])
                || hasRegionalAny(context, ['high latitude'])
            ),
        reasons: () => ['Forest cover', 'Subarctic or treeline context']
    },
    {
        id: 'iucn-subantarctic-forest',
        label: 'Subantarctic Forest',
        family: 'Forest',
        priority: 225,
        confidence: 'medium',
        matches: (context) =>
            hasStrongForestCover(context) &&
            (
                hasRegionalAny(context, ['subantarctic'])
                || (hasRegionalAny(context, ['southern hemisphere']) && hasRegionalAny(context, ['high latitude']))
            ),
        reasons: () => ['Forest cover', 'Subantarctic or cool southern-oceanic context']
    },
    {
        id: 'iucn-alpine-wetland',
        label: 'Alpine Wetland',
        family: 'Wetland',
        priority: 220,
        confidence: 'medium',
        matches: (context) =>
            hasWetlandCover(context) &&
            hasLocalAny(context, ['alpine', 'subalpine', 'montane', 'high elevation', 'snowmelt', 'tarn', 'mountain meadow']),
        reasons: () => ['Wetland or marsh cover', 'Alpine or high-elevation wetland context']
    },
    {
        id: 'iucn-tundra-wetland',
        label: 'Tundra Wetland',
        family: 'Wetland',
        priority: 215,
        confidence: 'medium',
        matches: (context) =>
            hasWetlandCover(context) &&
            (
                hasLocalAny(context, ['tundra', 'permafrost'])
                || hasRegionalValue(context, ['arctic', 'subarctic', 'polar'])
                || hasRegionalAny(context, ['tundra'])
            ),
        reasons: () => ['Wetland or marsh cover', 'Tundra, arctic, subarctic, or polar context']
    },
    {
        id: 'iucn-peatland-marsh-swamp',
        label: 'Peatland Marsh and Swamp',
        family: 'Wetland',
        priority: 210,
        confidence: 'low',
        matches: (context) =>
            hasWetlandCover(context) &&
            hasLocalAny(context, ['bog', 'fen', 'peatland', 'peat', 'mire', 'muskeg', 'pocosin']),
        reasons: () => ['Wetland or marsh cover', 'Peatland, bog, fen, or swamp signals']
    },
    {
        id: 'iucn-shrub-wetland',
        label: 'Shrub Wetland',
        family: 'Wetland',
        priority: 205,
        confidence: 'medium',
        matches: (context) =>
            hasWetlandCover(context) &&
            hasLocalAny(context, ['shrub', 'scrub', 'thicket', 'willow', 'alder']),
        reasons: () => ['Wetland or marsh cover', 'Shrub-dominated wetland context']
    },
    {
        id: 'iucn-permanent-freshwater-marsh',
        label: 'Permanent Freshwater Marsh',
        family: 'Wetland',
        priority: 200,
        confidence: 'medium',
        matches: (context) =>
            hasWetlandCover(context) &&
            (
                hasLocalAny(context, ['permanent', 'freshwater', 'emergent'])
                || hasLocalAny(context, ['pond', 'lake-edge', 'riverine', 'river', 'pond edge'])
                || hasLocalAny(context, ['at water / water edge', 'on the shoreline', 'near water'])
                || hasDominantFreshHerbaceousWetland(context)
            ) &&
            !hasConfirmedOceanContext(context) &&
            !hasLocalAny(context, ['seasonal', 'ephemeral', 'intermittent', 'vernal', 'playa', 'saline', 'brackish']),
        reasons: () => ['Marsh or wetland cover', 'Permanent freshwater or inland-water context']
    },
    {
        id: 'iucn-seasonal-freshwater-marsh',
        label: 'Seasonal Freshwater Marsh',
        family: 'Wetland',
        priority: 195,
        confidence: 'low',
        matches: (context) =>
            hasWetlandCover(context) &&
            hasLocalAny(context, ['seasonal', 'intermittent', 'ephemeral', 'vernal', 'flooded', 'pothole', 'slough']) &&
            !hasConfirmedOceanContext(context),
        reasons: () => ['Marsh or wetland cover', 'Seasonal, intermittent, or ephemeral freshwater context']
    },
    {
        id: 'iucn-saline-inland-lake',
        label: 'Saline Inland Lake',
        family: 'Water',
        priority: 190,
        confidence: 'low',
        matches: (context) =>
            hasWaterCover(context) &&
            hasLocalAny(context, ['saline', 'brackish', 'alkaline', 'salt lake', 'playa lake', 'soda lake']) &&
            !hasLocalAny(context, ['coastal', 'estuary', 'lagoon', 'ocean', 'sea', 'aquaculture', 'shrimp pond', 'prawn pond', 'fish pond']),
        reasons: () => ['Open inland water context', 'Saline, brackish, or alkaline lake signals']
    },
    {
        id: 'iucn-saline-inland-flat',
        label: 'Saline Inland Flat',
        family: 'Wetland',
        priority: 185,
        confidence: 'low',
        matches: (context) =>
            (
                hasWetlandCover(context)
                || hasLocalAny(context, ['salt flat', 'playa', 'alkaline flat', 'brackish flat', 'bare', 'sparsely vegetated'])
            ) &&
            hasLocalAny(context, ['salt flat', 'playa', 'alkaline flat', 'brackish flat', 'seasonal saline lake']),
        reasons: () => ['Inland flat or wetland context', 'Saline, playa, or alkaline-flat signals']
    },
    {
        id: 'iucn-saline-inland-marsh',
        label: 'Saline Inland Marsh',
        family: 'Wetland',
        priority: 180,
        confidence: 'low',
        matches: (context) =>
            hasWetlandCover(context) &&
            hasLocalAny(context, ['saline', 'brackish', 'alkaline', 'salt marsh', 'playa marsh', 'mineral wetland']) &&
            !hasLocalAny(context, ['seasonal', 'intermittent', 'ephemeral']),
        reasons: () => ['Marsh or wetland cover', 'Saline, brackish, or alkaline inland-marsh context']
    },
    {
        id: 'iucn-seasonal-saline-inland-marsh',
        label: 'Seasonal Saline Inland Marsh',
        family: 'Wetland',
        priority: 178,
        confidence: 'low',
        matches: (context) =>
            hasWetlandCover(context) &&
            hasLocalAny(context, ['seasonal', 'intermittent', 'playa', 'saline', 'brackish', 'alkaline', 'temporary mineral wetland']),
        reasons: () => ['Marsh or wetland cover', 'Seasonal saline or brackish inland-marsh context']
    },
    {
        id: 'coastal-water-edge',
        label: 'Coastal Water Edge',
        family: 'Coastal',
        priority: 176,
        confidence: 'medium',
        matches: (context) =>
            hasStrongCoastalContext(context) &&
            (
                hasWaterCover(context)
                || hasLocalAny(context, ['at water / water edge', 'on the shoreline', 'near water', 'water edge'])
            ) &&
            !hasConfirmedOceanContext(context) &&
            !hasLocalAny(context, ['aquaculture', 'fish pond', 'shrimp pond', 'prawn pond', 'culture pond', 'mariculture cage', 'net pen']),
        reasons: () => ['Water or water-edge context', 'Strong coastline signal without a more specific coastal match']
    },
    {
        id: 'nearshore-marine-water',
        label: 'Nearshore Marine Water',
        family: 'Water',
        priority: 175,
        confidence: 'high',
        matches: (context) =>
            hasNearshoreMarineContext(context),
        reasons: () => ['Ocean polygon match', 'Nearshore marine worker context']
    },
    {
        id: 'pelagic-water',
        label: 'Pelagic Water',
        family: 'Water',
        priority: 174,
        confidence: 'high',
        matches: (context) =>
            hasPelagicMarineContext(context),
        reasons: () => ['Ocean polygon match', 'Pelagic marine worker context']
    },
    {
        id: 'iucn-permanent-freshwater-lake',
        label: 'Permanent Freshwater Lake',
        family: 'Water',
        priority: 175,
        confidence: 'medium',
        matches: (context) =>
            hasWaterCover(context) &&
            hasExplicitLakeEvidence(context) &&
            !(
                hasDirectWaterEdgeContext(context)
                && hasMeaningfulForestMatrix(context)
                && ((getWaterFraction(context) ?? 1) < 0.85)
            ) &&
            !hasStrongCoastalContext(context) &&
            !hasConfirmedOceanContext(context) &&
            !hasArtificialWaterContext(context) &&
            !hasLocalAny(context, ['seasonal', 'intermittent', 'ephemeral', 'saline', 'brackish']),
        reasons: () => ['Open water context', 'Permanent freshwater-lake signals']
    },
    {
        id: 'iucn-seasonal-freshwater-lake',
        label: 'Seasonal Freshwater Lake',
        family: 'Water',
        priority: 170,
        confidence: 'low',
        matches: (context) =>
            hasWaterCover(context) &&
            hasLocalAny(context, ['seasonal', 'intermittent', 'floodplain lake', 'oxbow', 'temporary lake', 'ephemeral lake']) &&
            !hasConfirmedOceanContext(context) &&
            !hasStrongCoastalContext(context),
        reasons: () => ['Open water context', 'Seasonal or ephemeral freshwater-lake signals']
    },
    {
        id: 'iucn-permanent-river-stream',
        label: 'Permanent River or Stream',
        family: 'Water',
        priority: 165,
        confidence: 'medium',
        matches: (context) =>
            hasWaterCover(context) &&
            hasLocalAny(context, ['river', 'stream', 'creek', 'channel', 'flowing water', 'riparian corridor', 'watercourse']) &&
            !hasConfirmedOceanContext(context) &&
            !hasLocalAny(context, ['seasonal', 'intermittent', 'ephemeral', 'wash', 'arroyo']),
        reasons: () => ['Water context', 'Permanent river, stream, or flowing-water signals']
    },
    {
        id: 'iucn-seasonal-river-stream',
        label: 'Seasonal River or Stream',
        family: 'Water',
        priority: 160,
        confidence: 'low',
        matches: (context) =>
            hasWaterCover(context) &&
            hasLocalAny(context, ['river', 'stream', 'creek', 'wash', 'arroyo', 'intermittent', 'seasonal', 'ephemeral', 'watercourse']) &&
            !hasConfirmedOceanContext(context),
        reasons: () => ['Water context', 'Seasonal, intermittent, or ephemeral watercourse signals']
    },
    {
        id: 'iucn-freshwater-spring-oasis',
        label: 'Freshwater Spring or Oasis',
        family: 'Water',
        priority: 155,
        confidence: 'low',
        matches: (context) =>
            hasWaterCover(context) &&
            hasLocalAny(context, ['spring', 'seep', 'oasis', 'artesian', 'groundwater']) &&
            !hasLocalAny(context, ['geothermal', 'hot spring', 'thermal spring', 'geyser', 'fumarole']),
        reasons: () => ['Water context', 'Spring, seep, oasis, or groundwater-source signals']
    },
    {
        id: 'iucn-geothermal-wetland',
        label: 'Geothermal Wetland',
        family: 'Wetland',
        priority: 153,
        confidence: 'low',
        matches: (context) =>
            (hasWetlandCover(context) || hasWaterCover(context)) &&
            hasLocalAny(context, ['geothermal', 'hot spring', 'thermal spring', 'geyser', 'fumarole', 'warm spring']),
        reasons: () => ['Wetland or water context', 'Geothermal or thermal-water signals']
    },
    {
        id: 'iucn-inland-delta',
        label: 'Inland Delta',
        family: 'Wetland',
        priority: 150,
        confidence: 'low',
        matches: (context) =>
            (hasWetlandCover(context) || hasWaterCover(context)) &&
            hasLocalAny(context, ['delta', 'distributary', 'alluvial fan wetland', 'terminal basin', 'inland floodplain']),
        reasons: () => ['Wetland or inland-water context', 'Delta, distributary, or inland-floodplain signals']
    },
    {
        id: 'iucn-karst-inland-subterranean-aquatic-system',
        label: 'Karst and Inland Subterranean Aquatic System',
        family: 'Water',
        priority: 73,
        confidence: 'low',
        matches: (context) =>
            (hasWaterCover(context) || hasLocalAny(context, ['groundwater'])) &&
            hasLocalAny(context, ['karst', 'cave stream', 'sinkhole', 'cenote', 'subterranean river', 'groundwater system']),
        reasons: () => ['Inland aquatic context', 'Karst, sinkhole, cave-stream, or subterranean-water signals']
    },
    {
        id: 'iucn-inland-rocky-area',
        label: 'Inland Rocky Area',
        family: 'Mountain',
        priority: 75,
        confidence: 'medium',
        matches: (context) =>
            hasRockyCover(context) &&
            (
                hasLocalAny(context, ['mountain', 'canyon', 'inland cliff', 'scree', 'talus', 'bare rock', 'boulder'])
                || !hasLocalAny(context, ['coastal', 'shore', 'beach', 'intertidal', 'reef'])
            ) &&
            !hasLocalAny(context, ['cave', 'cavern', 'grotto', 'lava tube', 'karst cave', 'subterranean', 'mine', 'tunnel', 'crevice', 'underground', 'subsurface']),
        reasons: () => ['Rocky, scree, talus, cliff, or bare-rock cover', 'Inland rocky terrain context']
    },
    {
        id: 'iucn-dry-cave',
        label: 'Dry Cave',
        family: 'Mountain',
        priority: 72,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['cave', 'cavern', 'grotto', 'lava tube', 'karst cave']) &&
            !hasLocalAny(context, ['cave stream', 'subterranean river', 'spring', 'seep', 'sinkhole', 'cenote', 'water']),
        reasons: () => ['Cave or subterranean chamber signals', 'Dry non-aquatic subterranean context']
    },
    {
        id: 'iucn-other-dry-subterranean-habitat',
        label: 'Other Dry Subterranean Habitat',
        family: 'Mixed',
        priority: 71,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['subterranean', 'mine', 'tunnel', 'crevice', 'underground', 'subsurface']) &&
            !hasLocalAny(context, ['cave', 'cavern', 'grotto', 'lava tube', 'karst cave', 'cave stream', 'subterranean river', 'spring', 'seep', 'sinkhole', 'cenote', 'water']),
        reasons: () => ['Subterranean, mine, tunnel, or underground signals', 'Dry non-aquatic subsurface context']
    },
    {
        id: 'iucn-hot-desert',
        label: 'Hot Desert',
        family: 'Desert',
        priority: 70,
        confidence: 'high',
        matches: (context) =>
            hasDesertCover(context) &&
            (
                hasRegionalAny(context, ['tropical', 'subtropical', 'sonoran', 'sahara', 'chihuahuan'])
                || hasLocalAny(context, ['hot'])
            ) &&
            !hasLocalAny(context, ['cold', 'alpine', 'subalpine', 'snow']),
        reasons: () => ['Desert, dune, sand, xeric, or arid cover', 'Hot tropical or subtropical desert context']
    },
    {
        id: 'iucn-temperate-desert',
        label: 'Temperate Desert',
        family: 'Desert',
        priority: 65,
        confidence: 'medium',
        matches: (context) =>
            hasDesertCover(context) &&
            hasExplicitDesertAriditySignal(context) &&
            (
                hasRegionalValue(context, ['temperate', 'mid latitude'])
                || hasRegionalAny(context, ['great basin', 'cold-season', 'temperate desert', 'rain shadow', 'rain-shadow'])
                || hasLocalAny(context, ['temperate desert', 'rain shadow', 'rain-shadow'])
            ) &&
            !hasRegionalValue(context, ['tropical', 'subtropical', 'polar'])
            && !hasLocalAny(context, ['alpine', 'subalpine', 'snow', 'glacial']),
        reasons: () => ['Explicit desert or arid cover', 'Temperate or mid-latitude desert context']
    },
    {
        id: 'iucn-cold-desert',
        label: 'Cold Desert',
        family: 'Desert',
        priority: 60,
        confidence: 'medium',
        matches: (context) =>
            hasDesertCover(context) &&
            (
                hasLocalAny(context, ['cold', 'rain-shadow'])
                || hasRegionalAny(context, ['gobi', 'patagonian'])
                || hasRegionalValue(context, ['polar'])
                || hasLocalAny(context, ['alpine', 'subalpine', 'high elevation'])
            ),
        reasons: () => ['Desert or bare arid cover', 'Cold, high-elevation, polar, or rain-shadow desert context']
    },
    {
        id: 'iucn-neritic-pelagic-water',
        label: 'Neritic Pelagic Water',
        family: 'Water',
        priority: 58,
        confidence: 'low',
        matches: (context) =>
            hasMarineWaterContext(context) &&
            hasLocalAny(context, ['nearshore', 'bay', 'gulf', 'shelf', 'sea', 'ocean', 'pelagic']) &&
            !hasShorelineSubstrate(context) &&
            !hasLocalAny(context, ['open ocean', 'offshore']),
        reasons: () => ['Marine or coastal open-water context', 'Nearshore, bay, gulf, shelf, sea, or pelagic signals without substrate cues']
    },
    {
        id: 'iucn-subtidal-rocky-reef',
        label: 'Subtidal Rocky Reef',
        family: 'Water',
        priority: 57,
        confidence: 'low',
        matches: (context) =>
            hasMarineWaterContext(context) &&
            hasLocalAny(context, ['subtidal rock', 'rocky reef', 'reef', 'underwater rock', 'hard-bottom']),
        reasons: () => ['Marine or coastal water context', 'Subtidal rock, reef, or hard-bottom signals']
    },
    {
        id: 'iucn-oceanic-pelagic-water',
        label: 'Oceanic Pelagic Water',
        family: 'Water',
        priority: 56,
        confidence: 'low',
        matches: (context) =>
            hasMarineWaterContext(context) &&
            hasLocalAny(context, ['open ocean', 'offshore', 'pelagic', 'sea', 'ocean']) &&
            !hasLocalAny(context, ['nearshore', 'bay', 'gulf', 'shelf']) &&
            !hasShorelineSubstrate(context),
        reasons: () => ['Open marine water context', 'Offshore, sea, ocean, or pelagic signals away from shoreline substrate']
    },
    {
        id: 'iucn-arable-land',
        label: 'Arable Land',
        family: 'Agricultural',
        priority: 55,
        confidence: 'high',
        matches: (context) =>
            hasAgriculturalContext(context) &&
            hasLocalAny(context, ['cropland', 'farm', 'field', 'row crop', 'cultivated', 'arable']) &&
            !hasLocalAny(context, ['pasture', 'hayfield', 'rangeland', 'paddock', 'plantation', 'orchard', 'garden', 'aquaculture', 'fish pond', 'shrimp pond', 'prawn pond', 'irrigated', 'canal-fed agriculture', 'rice', 'paddy']),
        reasons: () => ['Agricultural context', 'Cropland, field, row-crop, cultivated, or arable signals']
    },
    {
        id: 'iucn-pastureland',
        label: 'Pastureland',
        family: 'Agricultural',
        priority: 50,
        confidence: 'medium',
        matches: (context) =>
            hasAgriculturalContext(context) &&
            hasLocalAny(context, ['pasture', 'hayfield', 'grazed grassland', 'rangeland', 'paddock', 'livestock', 'managed grass']),
        reasons: () => ['Agricultural context', 'Pasture, hayfield, rangeland, paddock, or livestock-management signals']
    },
    {
        id: 'iucn-plantation',
        label: 'Plantation',
        family: 'Agricultural',
        priority: 45,
        confidence: 'low',
        matches: (context) =>
            hasAgriculturalContext(context) &&
            hasLocalAny(context, ['plantation', 'orchard', 'tree crop', 'oil palm', 'eucalyptus', 'timber plantation', 'coffee', 'cocoa']),
        reasons: () => ['Agricultural context', 'Plantation, orchard, tree-crop, or managed-tree signals']
    },
    {
        id: 'iucn-rural-garden',
        label: 'Rural Garden',
        family: 'Agricultural',
        priority: 43,
        confidence: 'low',
        matches: (context) =>
            hasAgriculturalContext(context) &&
            hasLocalAny(context, ['garden', 'rural garden', 'allotment', 'homestead garden', 'village garden', 'smallholder']),
        reasons: () => ['Agricultural or cultivated context', 'Garden, allotment, homestead, village, or smallholder signals']
    },
    {
        id: 'iucn-urban-area',
        label: 'Urban Area',
        family: 'Urban',
        priority: 40,
        confidence: 'high',
        matches: (context) =>
            hasUrbanContext(context) &&
            hasLocalAny(context, ['urban', 'built-up', 'built up', 'developed', 'settlement', 'residential', 'industrial', 'road-dense', 'city']),
        reasons: () => ['Urban or developed context', 'Built-up, settlement, residential, industrial, or city signals']
    },
    {
        id: 'iucn-heavily-degraded-tropical-forest',
        label: 'Heavily Degraded Tropical Forest',
        family: 'Forest',
        priority: 38,
        confidence: 'low',
        matches: (context) =>
            hasStrongForestCover(context) &&
            hasRegionalAny(context, ['tropical', 'subtropical', 'neotropical', 'afrotropical', 'indo-malayan', 'australasian']) &&
            hasLocalAny(context, ['degraded', 'disturbed', 'secondary', 'cleared', 'fragmented', 'regrowth', 'heavily altered']),
        reasons: () => ['Tropical or subtropical forest context', 'Degraded, secondary, fragmented, or regrowth signals']
    },
    {
        id: 'iucn-water-storage-area',
        label: 'Water Storage Area',
        family: 'Water',
        priority: 35,
        confidence: 'low',
        matches: (context) =>
            hasArtificialWaterContext(context) &&
            hasLocalAny(context, ['reservoir', 'impoundment', 'dam', 'water storage', 'retention basin', 'artificial lake']),
        reasons: () => ['Artificial water context', 'Reservoir, impoundment, dam, storage, or retention-basin signals']
    },
    {
        id: 'iucn-pond',
        label: 'Pond',
        family: 'Water',
        priority: 30,
        confidence: 'low',
        matches: (context) =>
            (hasWaterCover(context) || hasArtificialWaterContext(context)) &&
            hasLocalAny(context, ['pond', 'small water body', 'retention pond', 'farm pond', 'ornamental pond']) &&
            !hasConfirmedOceanContext(context) &&
            !hasLocalAny(context, ['aquaculture', 'shrimp pond', 'fish pond', 'salt pond', 'brackish']),
        reasons: () => ['Still-water context', 'Pond, retention pond, farm pond, or ornamental-pond signals']
    },
    {
        id: 'iucn-aquaculture-pond',
        label: 'Aquaculture Pond',
        family: 'Agricultural',
        priority: 25,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['aquaculture', 'fish pond', 'shrimp pond', 'hatchery pond', 'managed pond', 'pond grid']) &&
            !hasLocalAny(context, ['brackish aquaculture', 'prawn pond', 'coastal culture pond', 'saline aquaculture pond']),
        reasons: () => ['Managed-water or agricultural context', 'Aquaculture, fish-pond, shrimp-pond, hatchery, or managed-pond signals']
    },
    {
        id: 'iucn-salt-exploitation-site',
        label: 'Salt Exploitation Site',
        family: 'Agricultural',
        priority: 24,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['salt pond', 'salt pan', 'salt works', 'salina', 'evaporation pond', 'managed saline production']),
        reasons: () => ['Managed saline context', 'Salt pond, salt pan, salina, works, or evaporation-pond signals']
    },
    {
        id: 'iucn-open-excavation',
        label: 'Open Excavation',
        family: 'Urban',
        priority: 23,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['quarry', 'gravel pit', 'mine', 'excavation', 'borrow pit', 'landfill excavation', 'open extraction']),
        reasons: () => ['Disturbed or developed context', 'Quarry, gravel-pit, mine, excavation, or borrow-pit signals']
    },
    {
        id: 'iucn-wastewater-treatment-area',
        label: 'Wastewater Treatment Area',
        family: 'Urban',
        priority: 22,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['wastewater', 'sewage pond', 'treatment pond', 'settling basin', 'oxidation pond', 'water treatment']),
        reasons: () => ['Managed-water or developed context', 'Wastewater, sewage, treatment, settling-basin, or oxidation-pond signals']
    },
    {
        id: 'iucn-irrigated-land',
        label: 'Irrigated Land',
        family: 'Agricultural',
        priority: 21,
        confidence: 'low',
        matches: (context) =>
            hasAgriculturalContext(context) &&
            hasLocalAny(context, ['irrigated field', 'irrigation', 'canal-fed agriculture', 'irrigated pasture', 'managed wet crop']),
        reasons: () => ['Agricultural context', 'Irrigation, canal-fed, irrigated-pasture, or managed-wet-crop signals']
    },
    {
        id: 'iucn-seasonally-flooded-agricultural-land',
        label: 'Seasonally Flooded Agricultural Land',
        family: 'Agricultural',
        priority: 20,
        confidence: 'low',
        matches: (context) =>
            hasAgriculturalContext(context) &&
            hasLocalAny(context, ['flooded', 'wet meadow', 'rice', 'paddy', 'irrigated', 'seasonal wetland', 'managed floodwater']),
        reasons: () => ['Agricultural or pasture context', 'Flooded, rice, paddy, irrigated, wet-meadow, or managed-floodwater signals']
    },
    {
        id: 'iucn-canal-drainage-channel',
        label: 'Canal or Drainage Channel',
        family: 'Water',
        priority: 15,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['canal', 'ditch', 'drainage channel', 'linear water', 'irrigation canal', 'artificial watercourse']),
        reasons: () => ['Artificial linear-water context', 'Canal, ditch, drainage-channel, or artificial-watercourse signals']
    },
    {
        id: 'iucn-human-made-subterranean-hydrological-system',
        label: 'Human-made Subterranean Hydrological System',
        family: 'Water',
        priority: 14,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['aqueduct', 'tunnel', 'underground canal', 'drainage tunnel', 'mine water', 'artificial subterranean hydrology']),
        reasons: () => ['Artificial subterranean-water context', 'Aqueduct, tunnel, underground-canal, drainage-tunnel, or mine-water signals']
    },
    {
        id: 'iucn-marine-anthropogenic-structure',
        label: 'Marine Anthropogenic Structure',
        family: 'Urban',
        priority: 13,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['pier', 'jetty', 'breakwater', 'seawall', 'offshore platform', 'marina', 'artificial reef', 'marine structure']),
        reasons: () => ['Marine developed context', 'Pier, jetty, breakwater, seawall, marina, platform, or artificial-reef signals']
    },
    {
        id: 'iucn-mariculture-cage',
        label: 'Mariculture Cage',
        family: 'Agricultural',
        priority: 12,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['mariculture cage', 'fish cage', 'salmon farm', 'sea pen', 'offshore aquaculture', 'net pen']),
        reasons: () => ['Managed marine-production context', 'Mariculture cage, fish-cage, salmon-farm, sea-pen, or net-pen signals']
    },
    {
        id: 'iucn-brackish-culture-pond',
        label: 'Brackish Culture Pond',
        family: 'Agricultural',
        priority: 11,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['brackish aquaculture', 'shrimp pond', 'prawn pond', 'coastal culture pond', 'saline aquaculture pond']),
        reasons: () => ['Managed brackish-water context', 'Shrimp, prawn, coastal-culture, brackish, or saline-aquaculture pond signals']
    },
    {
        id: 'iucn-introduced-vegetation',
        label: 'Introduced Vegetation',
        family: 'Shrubland',
        priority: 10,
        confidence: 'low',
        matches: (context) =>
            hasLocalAny(context, ['invasive', 'introduced vegetation', 'exotic scrub', 'non-native grass', 'disturbed vegetation', 'heavily altered vegetated cover']),
        reasons: () => ['Vegetated cover context', 'Invasive, introduced, exotic, non-native, or heavily altered vegetation signals']
    },
    {
        id: 'iucn-dry-savanna',
        label: 'Dry Savanna',
        family: 'Grassland',
        priority: 145,
        confidence: 'high',
        matches: (context) =>
            hasOpenGrassland(context) &&
            (
                hasLocalAny(context, ['dry', 'arid'])
                || hasRegionalAny(context, ['tropical dry', 'dry savanna', 'thornveld', 'miombo', 'cerrado', 'dry tropical', 'xeric'])
                || (hasRegionalAny(context, ['tropical', 'subtropical']) && hasLocalAny(context, ['savanna', 'open woodland']) && hasLocalAny(context, ['dry', 'thorn']))
            ) &&
            !hasLocalAny(context, ['wet', 'flooded', 'marsh', 'wetland', 'seasonally wet']),
        reasons: () => ['Savanna or open grassland structure', 'Dry tropical or subtropical context']
    },
    {
        id: 'iucn-moist-savanna',
        label: 'Moist Savanna',
        family: 'Grassland',
        priority: 140,
        confidence: 'high',
        matches: (context) =>
            hasOpenGrassland(context) &&
            (
                hasLocalAny(context, ['moist', 'seasonally wet', 'wet savanna'])
                || hasRegionalAny(context, ['moist savanna', 'seasonally wet savanna', 'wet savanna', 'tropical grassland', 'wooded grassland', 'seasonally wet'])
                || (hasRegionalAny(context, ['tropical', 'subtropical']) && hasLocalAny(context, ['wooded grassland', 'savanna']) && hasLocalAny(context, ['near water', 'water-influenced landscape']))
            ) &&
            !hasLocalAny(context, ['arid', 'xeric', 'desert']),
        reasons: () => ['Savanna or open grassland structure', 'Moist or seasonally wet tropical context']
    },
    {
        id: 'iucn-tundra',
        label: 'Tundra',
        family: 'Grassland',
        priority: 105,
        confidence: 'medium',
        matches: (context) =>
            (
                hasStrongOpenGrassland(context)
                || hasStructuralLocalAny(context, ['tundra', 'moss', 'lichen', 'permafrost'])
            ) &&
            (
                hasLocalAny(context, ['tundra', 'alpine tundra', 'moss', 'lichen', 'permafrost'])
                || hasRegionalValue(context, ['arctic', 'subarctic', 'polar'])
                || hasRegionalAny(context, ['tundra'])
            ) &&
            !hasLocalAny(context, ['wetland', 'marsh', 'bog', 'fen', 'mire', 'swamp']),
        reasons: () => ['Open low-stature ground cover', 'Arctic, subarctic, polar, or tundra context']
    },
    {
        id: 'iucn-high-altitude-tropical-grassland',
        label: 'High Altitude Tropical Grassland',
        family: 'Grassland',
        priority: 100,
        confidence: 'medium',
        matches: (context) =>
            hasStrongOpenGrassland(context) &&
            hasLocalAny(context, ['alpine', 'subalpine', 'montane', 'highland', 'paramo', 'puna', 'montane grassland']) &&
            hasRegionalAny(context, ['tropical', 'subtropical', 'paramo', 'puna']),
        reasons: () => ['Grassland or meadow structure', 'High-altitude tropical or subtropical context']
    },
    {
        id: 'iucn-flooded-lowland-grassland',
        label: 'Flooded Lowland Grassland',
        family: 'Grassland',
        priority: 95,
        confidence: 'medium',
        matches: (context) =>
            hasStrongOpenGrassland(context) &&
            (
                hasLocalAny(context, ['seasonally wet', 'flooded', 'floodplain', 'wet meadow', 'inundation'])
                || hasRegionalAny(context, ['flooded grasslands', 'wet meadow', 'floodplain'])
                || hasLocalAny(context, ['near water', 'water-influenced landscape'])
            ) &&
            hasLocalAny(context, ['lowland', 'floodplain', 'meadow', 'grassland', 'prairie', 'savanna']) &&
            !hasLocalAny(context, ['salt', 'saline', 'brackish']),
        reasons: () => ['Grassland or meadow structure', 'Seasonally wet or flooded lowland context']
    },
    {
        id: 'managed-parkland',
        label: 'Managed Parkland',
        family: 'Urban',
        kind: getRuleKind('managed-parkland'),
        priority: 90,
        confidence: 'medium',
        matches: (context) =>
            hasManagedParklandExplicitSignal(context) &&
            hasManagedParklandStructure(context) &&
            !hasDominantNaturalManagedParklandExclusion(context),
        reasons: getManagedParklandReasons
    },
    {
        id: 'iucn-dry-lowland-tropical-grassland',
        label: 'Dry Lowland Tropical Grassland',
        family: 'Grassland',
        priority: 90,
        confidence: 'medium',
        matches: (context) =>
            hasStrongOpenGrassland(context) &&
            hasRegionalAny(context, ['tropical', 'subtropical']) &&
            (
                hasLocalAny(context, ['dry', 'tussock', 'hummock'])
                || hasRegionalAny(context, ['dry prairie', 'dry grassland', 'tropical dry', 'xeric'])
            ) &&
            !hasLocalAny(context, ['montane', 'subalpine', 'alpine', 'wet', 'flooded', 'marsh', 'wetland']),
        reasons: () => ['Grassland or open-country structure', 'Dry tropical or subtropical lowland context']
    },
    {
        id: 'iucn-temperate-grassland',
        label: 'Temperate Grassland',
        family: 'Grassland',
        priority: 85,
        confidence: 'high',
        matches: (context) =>
            hasStrongOpenGrassland(context) &&
            (
                hasRegionalValue(context, ['temperate'])
                || hasLocalAny(context, ['prairie', 'meadow', 'steppe', 'pampas', 'veld'])
                || hasRegionalAny(context, ['temperate grasslands', 'prairie', 'steppe', 'pampas', 'veld'])
            ) &&
            !hasRegionalValue(context, ['tropical', 'subtropical', 'boreal', 'subarctic', 'subantarctic', 'arctic', 'polar']) &&
            !hasRegionalAny(context, ['tundra']) &&
            !hasExplicitAgriculturalUse(context),
        reasons: () => ['Grassland, prairie, meadow, or steppe structure', 'Temperate open-country context']
    },
    {
        id: 'iucn-subarctic-grassland',
        label: 'Subarctic Grassland',
        family: 'Grassland',
        priority: 80,
        confidence: 'medium',
        matches: (context) =>
            hasStrongOpenGrassland(context) &&
            (
                hasRegionalValue(context, ['subarctic', 'high latitude'])
                || hasLocalAny(context, ['northern meadow', 'polar meadow', 'tundra-edge'])
            ) &&
            !hasLocalAny(context, ['wetland', 'marsh', 'bog', 'fen', 'mire', 'swamp']),
        reasons: () => ['Grassland or open-country structure', 'Subarctic or northern meadow context']
    },
    {
        id: 'rural-settlement',
        label: 'Rural Settlement',
        family: 'Urban',
        kind: getRuleKind('rural-settlement'),
        priority: 78,
        confidence: 'medium',
        matches: hasRuralSettlementSignal,
        reasons: getRuralSettlementReasons
    },
    {
        id: 'iucn-subantarctic-grassland',
        label: 'Subantarctic Grassland',
        family: 'Grassland',
        priority: 78,
        confidence: 'low',
        matches: (context) =>
            hasStrongOpenGrassland(context) &&
            (
                hasRegionalValue(context, ['subantarctic', 'cool oceanic', 'southern island'])
                || (hasRegionalValue(context, ['southern hemisphere']) && hasRegionalValue(context, ['high latitude']))
            ) &&
            !hasLocalAny(context, ['wetland', 'marsh', 'bog', 'fen', 'mire', 'swamp']),
        reasons: () => ['Grassland or open-country structure', 'Subantarctic or cool southern-oceanic context']
    },
    {
        id: 'fynbos',
        label: 'Fynbos',
        family: 'Shrubland',
        priority: 17,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (
                hasLocalAny(context, ['fynbos', 'renosterveld'])
                || hasRegionalAny(context, ['fynbos', 'renosterveld', 'cape floristic', 'cape fold', 'southwestern cape', 'south-western cape'])
            ),
        reasons: () => ['Shrubland or heath structure', 'Cape floristic, fynbos, or renosterveld regional context']
    },
    {
        id: 'chaparral',
        label: 'Chaparral',
        family: 'Shrubland',
        priority: 16.5,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (
                hasRegionalAny(context, [
                    'california chaparral',
                    'california coastal sage',
                    'chaparral and woodlands',
                    'baja california',
                    'california montane chaparral'
                ])
                || (
                    hasRegionalAny(context, ['california', 'baja california'])
                    && hasLocalAny(context, ['chaparral', 'coastal scrub', 'coastal sage', 'matorral'])
                )
            ),
        reasons: () => ['Shrubland or scrub structure', 'California or Baja chaparral regional context']
    },
    {
        id: 'matorral',
        label: 'Matorral',
        family: 'Shrubland',
        priority: 16.4,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (
                hasLocalAny(context, ['matorral'])
                || hasRegionalAny(context, ['chilean matorral', 'matorral', 'chile mediterranean'])
            ),
        reasons: () => ['Shrubland or scrub structure', 'Chilean matorral regional context']
    },
    {
        id: 'maquis',
        label: 'Maquis',
        family: 'Shrubland',
        priority: 16.3,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (
                hasLocalAny(context, ['maquis'])
                || hasRegionalAny(context, ['maquis', 'mediterranean basin', 'corsica', 'sardinia', 'tyrrhenian'])
            ),
        reasons: () => ['Shrubland or scrub structure', 'Mediterranean-basin maquis regional context']
    },
    {
        id: 'kwongan',
        label: 'Kwongan',
        family: 'Shrubland',
        priority: 16.2,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (
                hasLocalAny(context, ['kwongan'])
                || hasRegionalAny(context, ['kwongan', 'southwest australia', 'south-west australia', 'southwestern australia', 'swan coastal plain'])
            ),
        reasons: () => ['Shrubland or heath structure', 'Southwest-Australian kwongan regional context']
    },
    {
        id: 'sagebrush-steppe',
        label: 'Sagebrush Steppe',
        family: 'Shrubland',
        priority: 16,
        confidence: 'high',
        matches: (context) =>
            (hasStrongShrubCover(context) || hasStrongOpenGrassland(context)) &&
            (
                hasLocalAny(context, ['sagebrush', 'sage steppe'])
                || hasRegionalAny(context, ['sagebrush', 'sage steppe', 'great basin', 'wyoming basin', 'intermountain', 'columbia plateau'])
            ),
        reasons: () => ['Open shrub-steppe or grass-shrub structure', 'Sagebrush or interior western steppe regional context']
    },
    {
        id: 'boreal-muskeg',
        label: 'Boreal Muskeg',
        family: 'Wetland',
        priority: 15.8,
        confidence: 'high',
        matches: (context) =>
            (
                hasWetlandCover(context)
                || hasLocalAny(context, ['muskeg', 'bog', 'fen', 'peatland', 'peat', 'conifer swamp', 'black spruce'])
            ) &&
            (
                hasLocalAny(context, ['muskeg'])
                || hasRegionalAny(context, ['muskeg', 'boreal', 'taiga', 'hudson plain', 'boreal forest'])
            ),
        reasons: () => ['Wetland or peatland structure', 'Boreal muskeg, taiga, or northern peatland regional context']
    },
    {
        id: 'everglades-marsh',
        label: 'Everglades Marsh',
        family: 'Wetland',
        priority: 15.4,
        confidence: 'high',
        matches: (context) =>
            hasWetlandCover(context) &&
            (
                hasLocalAny(context, ['sawgrass', 'slough', 'glades'])
                || hasRegionalAny(context, ['everglades', 'south florida'])
            ) &&
            !hasStrongCoastalContext(context),
        reasons: () => ['Fresh marsh or slough structure', 'Everglades or south-Florida regional context']
    },
    {
        id: 'sonoran-desert-wash',
        label: 'Sonoran Desert Wash',
        family: 'Desert',
        priority: 15.6,
        confidence: 'high',
        matches: (context) =>
            hasRegionalAny(context, ['sonoran', 'sonora']) &&
            (
                hasLocalAny(context, ['wash', 'arroyo'])
                || (
                    hasLocalAny(context, ['river', 'riparian', 'stream', 'creek', 'oasis', 'water'])
                )
            ) &&
            (
                hasRegionalAny(context, ['deserts & xeric shrublands'])
                || hasLocalAny(context, ['desert', 'arid', 'xeric'])
            ),
        reasons: () => ['Wash, arroyo, or desert watercourse signal', 'Sonoran or hot-desert regional context']
    },
    {
        id: 'arctic-coastal-plain',
        label: 'Arctic Coastal Plain',
        family: 'Coastal',
        priority: 14.8,
        confidence: 'high',
        matches: (context) =>
            (
                hasLocalAny(context, ['coastal plain', 'barrier island tundra'])
                || (
                    hasNearCoastalContext(context)
                    && (hasRegionalAny(context, ['arctic', 'polar', 'north slope', 'coastal plain']) || hasRegionalAny(context, ['alaska tundra']))
                )
            ) &&
            (
                hasLocalAny(context, ['tundra', 'wet meadow', 'marsh', 'coastal plain'])
                || hasRegionalAny(context, ['arctic coastal plain', 'north slope coastal plain'])
            ),
        reasons: () => ['Near-coastal tundra or wet-meadow context', 'Arctic coastal plain regional context']
    },
    {
        id: 'paramo',
        label: 'Páramo',
        family: 'Grassland',
        priority: 14.7,
        confidence: 'high',
        matches: (context) =>
            (hasStrongOpenGrassland(context) || hasStrongShrubCover(context) || hasWetlandCover(context))
            && (
                hasLocalAny(context, ['paramo', 'páramo'])
                || hasRegionalAny(context, ['paramo', 'páramo', 'northern andean paramo', 'andean paramo'])
            )
            && hasRegionalAny(context, ['tropical', 'subtropical', 'andean', 'neotropical']),
        reasons: () => ['High-elevation tropical grassland, shrubland, or wetland structure', 'Andean páramo regional context']
    },
    {
        id: 'puna',
        label: 'Puna',
        family: 'Grassland',
        priority: 14.6,
        confidence: 'high',
        matches: (context) =>
            (hasStrongOpenGrassland(context) || hasStrongShrubCover(context))
            && (
                hasLocalAny(context, ['puna'])
                || hasRegionalAny(context, ['puna', 'central andean puna', 'dry puna', 'wet puna'])
            )
            && hasRegionalAny(context, ['tropical', 'subtropical', 'andean', 'neotropical']),
        reasons: () => ['High-elevation tropical grassland or shrubland structure', 'Andean puna regional context']
    },
    {
        id: 'iucn-mediterranean-shrubland',
        label: 'Mediterranean Shrubland',
        family: 'Shrubland',
        priority: 135,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (
                hasLocalAny(context, ['chaparral', 'matorral', 'maquis'])
                || hasRegionalAny(context, ['mediterranean'])
            ),
        reasons: () => ['Shrubland cover', 'Mediterranean shrubland context']
    },
    {
        id: 'iucn-high-altitude-tropical-shrubland',
        label: 'High Altitude Tropical Shrubland',
        family: 'Shrubland',
        priority: 130,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            hasLocalAny(context, ['alpine', 'subalpine', 'montane', 'highland', 'heath']) &&
            hasRegionalAny(context, ['tropical', 'subtropical', 'paramo', 'puna']),
        reasons: () => ['Shrubland cover', 'High-altitude tropical or subtropical context']
    },
    {
        id: 'iucn-dry-tropical-shrubland',
        label: 'Dry Tropical Shrubland',
        family: 'Shrubland',
        priority: 125,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            hasRegionalAny(context, ['tropical', 'subtropical']) &&
            (hasLocalAny(context, ['dry', 'thorn', 'xeric']) || hasRegionalAny(context, ['thornscrub', 'caatinga', 'dry woodland', 'dry shrubland', 'deserts & xeric shrublands', 'xeric shrublands', 'mezquital'])),
        reasons: () => ['Shrubland cover', 'Dry tropical or subtropical shrub context']
    },
    {
        id: 'iucn-moist-tropical-shrubland',
        label: 'Moist Tropical Shrubland',
        family: 'Shrubland',
        priority: 120,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            hasRegionalAny(context, ['tropical', 'subtropical']) &&
            (hasLocalAny(context, ['moist', 'humid', 'thicket']) || hasRegionalAny(context, ['humid', 'moist shrub', 'secondary moist shrub'])),
        reasons: () => ['Shrubland cover', 'Moist tropical or subtropical shrub context']
    },
    {
        id: 'iucn-temperate-shrubland',
        label: 'Temperate Shrubland',
        family: 'Shrubland',
        priority: 115,
        confidence: 'high',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (
                hasRegionalAny(context, ['temperate'])
                || hasLocalAny(context, ['sagebrush', 'coastal scrub', 'heath'])
            ) &&
            !hasRegionalAny(context, ['tropical', 'subtropical', 'boreal', 'subarctic', 'subantarctic']),
        reasons: () => ['Shrubland cover', 'Temperate shrubland or heath context']
    },
    {
        id: 'iucn-boreal-shrubland',
        label: 'Boreal Shrubland',
        family: 'Shrubland',
        priority: 110,
        confidence: 'medium',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (hasRegionalAny(context, ['boreal', 'taiga']) || hasLocalAny(context, ['muskeg', 'northern willow', 'dwarf birch'])),
        reasons: () => ['Shrubland cover', 'Boreal or taiga shrub context']
    },
    {
        id: 'iucn-subarctic-shrubland',
        label: 'Subarctic Shrubland',
        family: 'Shrubland',
        priority: 108,
        confidence: 'medium',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (hasRegionalAny(context, ['subarctic']) || hasRegionalAny(context, ['high latitude']) || hasLocalAny(context, ['tundra shrub', 'dwarf birch', 'willow'])),
        reasons: () => ['Shrubland cover', 'Subarctic shrub context']
    },
    {
        id: 'iucn-subantarctic-shrubland',
        label: 'Subantarctic Shrubland',
        family: 'Shrubland',
        priority: 106,
        confidence: 'medium',
        matches: (context) =>
            hasStrongShrubCover(context) &&
            (hasRegionalAny(context, ['subantarctic']) || (hasRegionalAny(context, ['southern hemisphere']) && hasRegionalAny(context, ['high latitude']))),
        reasons: () => ['Shrubland cover', 'Subantarctic shrub context']
    },
    {
        id: 'lakeshore-woodland',
        label: 'Lakeshore Woodland',
        family: 'Forest',
        priority: 20,
        confidence: 'high',
        matches: (context) =>
            hasMeaningfulForestMatrix(context)
            && hasLakeshoreEdgeContext(context)
            && (
                context.waterContext === 'On the shoreline'
                || (getWaterFraction(context) ?? 0) > 0
                || hasWaterCover(context)
            ),
        reasons: () => ['Forest cover or matrix', 'Actual lakeshore or freshwater edge context']
    },
    {
        id: 'riverine-woodland',
        label: 'Riverine Woodland',
        family: 'Forest',
        priority: 19.5,
        confidence: 'high',
        matches: (context) => hasLocalAny(context, ['forest', 'woodland', 'tree']) && hasLocalAny(context, ['river', 'riparian', 'stream', 'creek', 'water']),
        reasons: () => ['Forest cover', 'River or riparian context']
    },
    {
        id: 'subalpine-conifer-forest',
        label: 'Subalpine Conifer Forest',
        family: 'Mountain',
        priority: 18,
        confidence: 'high',
        matches: (context) => hasLocalAny(context, ['forest', 'conifer', 'taiga', 'boreal']) && hasLocalAny(context, ['subalpine', 'alpine', 'montane']),
        reasons: () => ['Conifer or forest cover', 'Subalpine or high-elevation terrain']
    },
    {
        id: 'montane-forest',
        label: 'Montane Forest',
        family: 'Forest',
        priority: 17.5,
        confidence: 'high',
        matches: (context) => hasLocalAny(context, ['forest', 'woodland', 'tree']) && hasLocalAny(context, ['montane', 'foothill', 'mountain', 'highland']),
        reasons: () => ['Forest cover', 'Montane or foothill terrain']
    },
    {
        id: 'cloud-forest',
        label: 'Cloud Forest',
        family: 'Forest',
        priority: 17,
        confidence: 'high',
        matches: (context) => hasLocalAny(context, ['cloud forest', 'montane forest']) || (hasLocalAny(context, ['forest', 'woodland']) && hasRegionalAny(context, ['cloud', 'humid', 'mist'])),
        reasons: () => ['Forest cover', 'Cloud forest or humid montane signals']
    },
    {
        id: 'montane-grassland',
        label: 'Montane Grassland',
        family: 'Grassland',
        priority: 15.6,
        confidence: 'high',
        matches: hasMontaneGrasslandSignal,
        reasons: getMontaneGrasslandReasons
    },
    {
        id: 'desert-riparian-corridor',
        label: 'Desert Riparian Corridor',
        family: 'Desert',
        priority: 15.5,
        confidence: 'high',
        matches: (context) =>
            (
                hasLocalAny(context, ['desert', 'arid', 'xeric'])
                || hasRegionalAny(context, ['desert', 'arid', 'xeric', 'deserts & xeric shrublands', 'sonoran', 'mojave', 'chihuahuan'])
            )
            && hasLocalAny(context, ['river', 'riparian', 'stream', 'water', 'wash', 'arroyo', 'oasis']),
        reasons: () => ['Desert or arid regional matrix', 'Riparian stream, river, wash, arroyo, or oasis signal']
    },
    {
        id: 'pinyon-juniper-woodland',
        label: 'Pinyon-Juniper Woodland',
        family: 'Forest',
        priority: 14.5,
        confidence: 'high',
        matches: (context) => hasLocalAny(context, ['pinyon', 'juniper']) && hasLocalAny(context, ['woodland', 'forest', 'tree']),
        reasons: () => ['Pinyon or juniper cover', 'Woodland structure']
    },
    {
        id: 'oak-savanna',
        label: 'Oak Savanna',
        family: 'Grassland',
        priority: 14,
        confidence: 'high',
        matches: (context) => hasLocalAny(context, ['oak']) && hasLocalAny(context, ['savanna', 'open woodland', 'grassland']),
        reasons: () => ['Oak cover', 'Savanna or open-woodland structure']
    },
    {
        id: 'urban-green-space',
        label: 'Urban Green Space',
        family: 'Urban',
        priority: 13.5,
        confidence: 'medium',
        matches: (context) => hasLocalAny(context, ['urban', 'built-up', 'built up', 'developed', 'settlement']) && hasUrbanGreenSpaceSignal(context),
        reasons: () => ['Urban or developed setting', 'Tree cover or parkland']
    },
    {
        id: 'agricultural-edge',
        label: 'Agricultural Edge',
        family: 'Agricultural',
        priority: 13,
        confidence: 'medium',
        matches: (context) => hasLocalAny(context, ['agricultural', 'cropland', 'farm', 'field', 'plantation']) && hasLocalAny(context, ['edge', 'mosaic', 'grassland', 'wetland', 'woodland']),
        reasons: () => ['Agricultural land cover', 'Edge or mosaic habitat']
    },
    {
        id: 'iucn-other-habitat',
        label: 'Other Habitat',
        family: 'Mixed',
        priority: 5,
        confidence: 'medium',
        matches: (context) =>
            [
                context.primaryHabitat,
                context.dominantHabitatGroup,
                context.dominantWorldcoverLabel,
                context.waterContext,
                context.terrainSummary,
                context.biomeName,
                context.ecoregionName,
                context.realm,
                ...context.secondaryHabitats,
                ...context.habitatMixLabels
            ]
                .map(value => normalizeSearchText(value))
                .some(Boolean),
        reasons: () => ['Some habitat context was available', 'No more specific IUCN-aligned rule matched']
    },
    {
        id: 'iucn-unknown-habitat',
        label: 'Unknown Habitat',
        family: 'Mixed',
        priority: 0,
        confidence: 'low',
        matches: (context) =>
            ![
                context.primaryHabitat,
                context.dominantHabitatGroup,
                context.dominantWorldcoverLabel,
                context.waterContext,
                context.terrainSummary,
                context.biomeName,
                context.ecoregionName,
                context.realm,
                ...context.secondaryHabitats,
                ...context.habitatMixLabels
            ]
                .map(value => normalizeSearchText(value))
                .some(Boolean),
        reasons: () => ['No usable habitat, land-cover, water, terrain, biome, or ecoregion signal was available']
    }
];

const buildRuleMatch = (rule: HabitatClassificationRule, context: HabitatClassificationContext): BirdingHabitatRuleMatch => ({
    id: rule.id,
    label: rule.label,
    family: rule.family,
    kind: rule.kind ?? getRuleKind(rule.id),
    priority: rule.priority,
    confidence: rule.confidence,
    reasons: rule.reasons(context)
});

const getRankedMatchesForRules = (rules: HabitatClassificationRule[], context: HabitatClassificationContext) => {
    return [...rules]
        .sort((a, b) => b.priority - a.priority)
        .filter(rule => rule.matches(context))
        .map(rule => buildRuleMatch(rule, context));
};

export const CANONICAL_HABITAT_RULES = BIRDING_HABITAT_RULES.filter(rule => (rule.kind ?? getRuleKind(rule.id)) === 'canonical');
export const DERIVED_HABITAT_RULES = BIRDING_HABITAT_RULES.filter(rule => (rule.kind ?? getRuleKind(rule.id)) !== 'canonical');

export const getRankedMatchedHabitatRules = (context: HabitatClassificationContext) => {
    return getRankedMatchesForRules(BIRDING_HABITAT_RULES, context);
};

export const getRankedCanonicalHabitatRules = (context: HabitatClassificationContext) => {
    return getRankedMatchesForRules(CANONICAL_HABITAT_RULES, context);
};

export const getRankedDerivedHabitatRules = (context: HabitatClassificationContext) => {
    return getRankedMatchesForRules(DERIVED_HABITAT_RULES, context);
};

export const getRankedDerivedSubtypeHabitatRules = (context: HabitatClassificationContext) => {
    return getRankedDerivedHabitatRules(context).filter(rule => rule.kind === 'derived-subtype');
};

export const getRankedDerivedDisplayHabitatRules = (context: HabitatClassificationContext) => {
    return getRankedDerivedHabitatRules(context).filter(rule => rule.kind === 'derived-display');
};

export const getBirdingHabitatMatchExplanation = (context: HabitatClassificationContext): BirdingHabitatMatchExplanation => {
    const rankedMatches = getRankedMatchedHabitatRules(context);
    const canonicalMatches = getRankedCanonicalHabitatRules(context);
    const derivedMatches = getRankedDerivedHabitatRules(context);
    const derivedSubtypeMatches = getRankedDerivedSubtypeHabitatRules(context);
    const derivedDisplayMatches = getRankedDerivedDisplayHabitatRules(context);
    const winner = rankedMatches[0];
    const canonicalWinner = canonicalMatches[0];
    const derivedWinner = derivedMatches[0];
    const appliedDerivedWinner = derivedSubtypeMatches[0] ?? derivedDisplayMatches[0];
    const nextMatches = rankedMatches
        .filter(match => match.id !== winner?.id)
        .filter(match => match.id !== appliedDerivedWinner?.id)
        .slice(0, 3);
    const debug = {
        input: {
            primaryHabitat: context.primaryHabitat,
            secondaryHabitats: context.secondaryHabitats,
            habitatMixLabels: context.habitatMixLabels,
            dominantHabitatGroup: context.dominantHabitatGroup,
            dominantWorldcoverLabel: context.dominantWorldcoverLabel,
            habitatFamily: context.habitatFamily,
            elevationBand: context.elevationBand,
            terrainSummary: context.terrainSummary,
            waterContext: context.waterContext,
            distanceToCoastM: context.distanceToCoastM,
            coastalContext: context.coastalContext,
            isCoastal: context.isCoastal,
            isNearCoastal: context.isNearCoastal,
            coastlineConfidence: context.coastlineConfidence,
            marineContextSource: context.marineContextSource,
            marineContextIsOcean: context.marineContextIsOcean,
            marineContext: context.marineContext,
            marineContextConfidence: context.marineContextConfidence,
            hotspotName: context.hotspotName,
            hotspotNameTerms: context.hotspotNameTerms,
            latitude: context.latitude,
            longitude: context.longitude,
            coordinateTerms: context.coordinateTerms,
            biomeName: context.biomeName,
            biomeTerms: context.biomeTerms,
            ecoregionName: context.ecoregionName,
            ecoregionTerms: context.ecoregionTerms,
            realm: context.realm,
            sourceFields: context.sourceFields
        },
        canonicalWinner,
        derivedWinner,
        appliedDerivedWinner,
        rankedMatches,
        canonicalMatches,
        derivedMatches
    };

    if (!winner) {
        return {
            winner: undefined,
            nextMatches: [],
            matchedCount: 0,
            summary: 'No legacy habitat rule matched this hotspot, so the classifier fell back to the general land-cover family.',
            debug
        };
    }

    const winningReasons = winner.reasons.length ? winner.reasons.join('; ') : 'no explicit reason text was available';
    const appliedDerivedText = appliedDerivedWinner
        ? ` Applied derived habitat ${appliedDerivedWinner.label} (${appliedDerivedWinner.kind}, priority ${appliedDerivedWinner.priority}).`
        : '';
    const runnerUpText = nextMatches.length
        ? `Next matches: ${nextMatches.map(match => `${match.label} (${match.priority})`).join(', ')}.`
        : 'No additional habitat rules matched this hotspot.';

    return {
        winner,
        nextMatches,
        matchedCount: rankedMatches.length,
        summary: `${winner.label} won on priority ${winner.priority} because it matched ${winningReasons}.${appliedDerivedText} ${runnerUpText}`,
        debug
    };
};

export const getMatchedHabitatRule = (context: HabitatClassificationContext) => {
    return getRankedMatchedHabitatRules(context)[0];
};

export const getMatchedCanonicalHabitatRule = (context: HabitatClassificationContext) => {
    return getRankedCanonicalHabitatRules(context)[0];
};

export const getMatchedDerivedHabitatRule = (context: HabitatClassificationContext) => {
    return getRankedDerivedHabitatRules(context)[0];
};

export const getMatchedDerivedSubtypeHabitatRule = (context: HabitatClassificationContext) => {
    return getRankedDerivedSubtypeHabitatRules(context)[0];
};

export const getMatchedDerivedDisplayHabitatRule = (context: HabitatClassificationContext) => {
    return getRankedDerivedDisplayHabitatRules(context)[0];
};
